import React, { useState, useEffect, useCallback } from 'react';

// AI token + cost monitor. Reads /api/admin/ai-usage/summary (admin-gated) and
// shows totals + breakdowns by function and by model for a date range.

const fmtCost = (c) => `$${Number(c || 0).toFixed(4)}`;
const fmtNum = (n) => Number(n || 0).toLocaleString();
const isoDay = (d) => d.toISOString().slice(0, 10);

function Card({ label, value }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-bold text-gray-900 mt-1">{value}</div>
    </div>
  );
}

function Table({ headers, rows }) {
  return (
    <div className="overflow-x-auto bg-white rounded-xl border border-gray-200">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-gray-500 border-b border-gray-100">
            {headers.map((h) => <th key={h} className="px-3 py-2 font-medium whitespace-nowrap">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={headers.length} className="px-3 py-3 text-gray-400">No data</td></tr>
          ) : rows.map((r, i) => (
            <tr key={i} className="border-b border-gray-50 last:border-0">
              {r.map((c, j) => (
                <td key={j} className={`px-3 py-2 whitespace-nowrap ${j === 0 ? 'text-gray-800 font-medium' : 'text-gray-600'}`}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AiUsagePanel() {
  const [from, setFrom] = useState(isoDay(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)));
  const [to, setTo] = useState(isoDay(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (to) params.set('to', `${to}T23:59:59`);
      const res = await fetch(`/api/admin/ai-usage/summary?${params.toString()}`, { credentials: 'include' });
      const j = await res.json().catch(() => ({}));
      if (res.ok && j.ok) setData(j);
      else setError(j.error || 'Failed to load usage.');
    } catch (_) {
      setError('Network error.');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const t = data?.totals;

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <h2 className="text-xl font-semibold text-gray-900">AI Usage &amp; Cost</h2>
        <div className="flex items-end gap-2">
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">From</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="px-2 py-1.5 rounded-lg border border-gray-300 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">To</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="px-2 py-1.5 rounded-lg border border-gray-300 text-sm" />
          </div>
          <button onClick={load} className="px-3 py-1.5 rounded-lg bg-[#00aef7] text-white text-sm font-semibold">Apply</button>
        </div>
      </div>

      {loading && <p className="text-gray-400">Loading…</p>}
      {error && <p className="text-red-600">{error}</p>}

      {data && !loading && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
            <Card label="Total cost" value={fmtCost(t?.cost)} />
            <Card label="Total tokens" value={fmtNum(t?.tokens)} />
            <Card label="API calls" value={fmtNum(t?.calls)} />
          </div>

          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">By function</h3>
            <Table
              headers={['Function', 'Calls', 'Prompt', 'Completion', 'Tokens', 'Cost']}
              rows={(data.byFunction || []).map((r) => [r.function_label, fmtNum(r.calls), fmtNum(r.prompt_tokens), fmtNum(r.completion_tokens), fmtNum(r.tokens), fmtCost(r.cost)])}
            />
          </div>

          <div className="mb-2">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">By model</h3>
            <Table
              headers={['Model', 'Calls', 'Tokens', 'Cost']}
              rows={(data.byModel || []).map((r) => [r.model, fmtNum(r.calls), fmtNum(r.tokens), fmtCost(r.cost)])}
            />
          </div>

          {(data.byFunction || []).length === 0 && (
            <p className="text-sm text-gray-400 mt-4">
              No AI calls recorded in this range yet. Usage is logged from the moment the monitor went live (historical spend lives in the OpenAI dashboard).
            </p>
          )}
        </>
      )}
    </div>
  );
}
